import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { IoArrowBack } from "react-icons/io5";
import { getImage, sortByAlphabetical } from "../../../../utils/Utils";
import { useGetProductListQuery } from "../../../../utils/api/product/product.api";
import { ApiBaseUrl } from "../../../../utils/http";
import { BestSellerCard } from "../../../HomePage/BestSellerCard";
import { ProductItemsSkeleton } from "../ParcoursAchat";
import ProduitDetailContent from "../../ParcoursAchat/ProduitDetailContent";

type ModalHistoryLevel = "root" | "sub" | "product";

type ModalHistoryState = {
  usr?: unknown;
  key?: string;
  idx?: number;
  categoryModal?: boolean;
  categoryModalLevel?: ModalHistoryLevel;
};

function pushModalHistory(level: ModalHistoryLevel) {
  const prev = (window.history.state || {}) as ModalHistoryState;
  const nextIdx =
    typeof prev.idx === "number" ? prev.idx + 1 : (window.history.length || 1);
  const nextState: ModalHistoryState = {
    ...prev,
    usr: prev.usr ?? null,
    key: prev.key || "category-modal",
    idx: nextIdx,
    categoryModal: true,
    categoryModalLevel: level,
  };
  window.history.pushState(nextState, "");
}

export interface SousCategorie {
  id: string | number;
  nom: string;
  slug: string;
  images?: string | { image: string }[] | null;
}

interface CategorySousCategorieModalProps {
  show: boolean;
  onClose: () => void;
  categoryName: string;
  categorySlug?: string;
  sousCategories: SousCategorie[];
  selectedSousCategories?: string[];
  onSelectSousCategorie: (slug: string) => void;
  favoriteSousCategories?: string[];
  onToggleFavorite?: (slug: string) => void;
  devise?: string;
}

function getSubCategoryImageUrl(path?: string | null) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${ApiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
}

function resolveSubImagePath(images: SousCategorie["images"]): string | null {
  if (!images) return null;
  if (typeof images === "string") {
    return getSubCategoryImageUrl(images);
  }
  if (Array.isArray(images) && images.length) {
    return getSubCategoryImageUrl(images[0]?.image);
  }
  return null;
}

interface SousCategorieCardProps {
  sub: SousCategorie;
  categorySlug?: string;
  isSelected: boolean;
  isFavorite: boolean;
  onSelect: () => void;
  onToggleFavorite?: (slug: string) => void;
}

const SousCategorieCard: React.FC<SousCategorieCardProps> = ({
  sub,
  categorySlug,
  isSelected,
  isFavorite,
  onSelect,
  onToggleFavorite,
}) => {
  const rawImagePath = resolveSubImagePath(sub?.images);

  const { data: fallbackData } = useGetProductListQuery(
    {
      page: 1,
      limit: 1,
      categorie: categorySlug,
      sous_categorie: [sub?.slug] as any,
    },
    { skip: !!rawImagePath || !categorySlug }
  );

  const fallbackProduct = fallbackData?.results?.[0];
  const fallbackProductImage = fallbackProduct
    ? fallbackProduct?.type === "taille_unique"
      ? fallbackProduct?.images?.[0]?.image
      : fallbackProduct?.variations?.[0]?.images?.[0]?.image
    : null;

  const imageUrl =
    rawImagePath || (fallbackProductImage ? getImage(fallbackProductImage) : null);

  return (
    <button
      type="button"
      className={`category-souscategorie-card ${
        isSelected ? "category-souscategorie-card--selected" : ""
      }`}
      onClick={onSelect}
    >
      <div className="category-souscategorie-card-image-wrapper">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={sub?.nom}
            className="category-souscategorie-card-image"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}

        {onToggleFavorite && (
          <span
            role="button"
            aria-label="Favoris"
            className="category-souscategorie-card-favorite"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(sub?.slug);
            }}
          >
            {isFavorite ? <AiFillHeart /> : <AiOutlineHeart />}
          </span>
        )}
      </div>

      <span className="category-souscategorie-card-name">{sub?.nom}</span>
    </button>
  );
};

const MOBILE_BREAKPOINT = 768;

const CategorySousCategorieModal: React.FC<CategorySousCategorieModalProps> = ({
  show,
  onClose,
  categoryName,
  categorySlug,
  sousCategories,
  selectedSousCategories = [],
  onSelectSousCategorie,
  favoriteSousCategories = [],
  onToggleFavorite,
  devise,
}) => {
  const sorted = sortByAlphabetical(sousCategories) || [];

  // Niveau 1 : sous-catégorie active
  const [activeSub, setActiveSub] = useState<SousCategorie | null>(null);
  // Niveau 2 : produit actif (affiché uniquement en mobile, dans le modal)
  const [activeProduct, setActiveProduct] = useState<any | null>(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  // Empile l'historique pour que Retour (navigateur / geste) ferme le modal
  // au lieu de quitter le site.
  const depthRef = useRef(0);
  const levelRef = useRef<ModalHistoryLevel>("root");
  const skipPopRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!show) return;

    depthRef.current = 1;
    levelRef.current = "root";
    pushModalHistory("root");

    const onPopState = () => {
      if (skipPopRef.current) {
        skipPopRef.current = false;
        depthRef.current = 0;
        levelRef.current = "root";
        return;
      }

      depthRef.current = Math.max(0, depthRef.current - 1);

      if (levelRef.current === "product") {
        setActiveProduct(null);
        levelRef.current = "sub";
        return;
      }
      if (levelRef.current === "sub") {
        setActiveSub(null);
        levelRef.current = "root";
        return;
      }

      setActiveSub(null);
      setActiveProduct(null);
      depthRef.current = 0;
      onCloseRef.current();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [show]);

  const { data: produitsData = { results: [], count: 0 }, isFetching: isFetchingProduits } =
    useGetProductListQuery(
      {
        page: 1,
        limit: 12,
        categorie: categorySlug,
        sous_categorie: [activeSub?.slug] as any,
      },
      { skip: !activeSub }
    );

  const produits = produitsData?.results || [];

  const handleSelectSub = (sub: SousCategorie) => {
    setActiveSub(sub);
    setActiveProduct(null);
    levelRef.current = "sub";
    depthRef.current += 1;
    pushModalHistory("sub");
  };

  const handleSelectProduct = (product: any) => {
    setActiveProduct(product);
    levelRef.current = "product";
    depthRef.current += 1;
    pushModalHistory("product");
  };

  // Flèche UI = même comportement que Retour navigateur
  const handleBack = () => {
    window.history.back();
  };

  const handleClose = () => {
    const depth = depthRef.current;
    setActiveSub(null);
    setActiveProduct(null);
    levelRef.current = "root";

    if (depth > 0) {
      skipPopRef.current = true;
      depthRef.current = 0;
      window.history.go(-depth);
    }

    onClose();
  };

  const showBackButton = !!activeSub || !!activeProduct;





  const headerTitle = activeProduct
    ? activeProduct?.nom
    : activeSub
    ? activeSub.nom
    : categoryName;

  const headerSubtitle = activeProduct
    ? ""
    : activeSub
    ? "Produits disponibles"
    : "Choisissez une sous-catégorie";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="category-souscategorie-modal"
      contentClassName="category-souscategorie-modal-content"
    >
      <div className="category-souscategorie-modal-header">
        <div className="category-souscategorie-modal-header-title-row">
          {showBackButton && (
            <button
              type="button"
              className="category-souscategorie-modal-back"
              aria-label="Retour"
              onClick={handleBack}
              style={{ border: "none", background: "transparent" }}
            >
              <IoArrowBack />
            </button>
          )}
          <div>
            <h3
              className="category-souscategorie-modal-title"
              style={{ border: "none", color: "black" }}
            >
              {headerTitle}
            </h3>
            {headerSubtitle && (
              <p className="category-souscategorie-modal-subtitle">
                {headerSubtitle}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          className="category-souscategorie-modal-close"
          aria-label="Fermer"
          onClick={handleClose}
        >
          &times;
        </button>
      </div>

      <div className="category-souscategorie-modal-body">
      {activeProduct ? (
  <ProduitDetailContent
    slug={activeProduct.slug}
    embedded
    onAddedToCart={handleClose}
    onBuyNow={handleClose}
  />
) : !activeSub ? (
          sorted.length ? (
            <div className="category-souscategorie-grid">
              {sorted.map((sub: SousCategorie) => {
                const isSelected = selectedSousCategories?.includes(sub?.slug);
                const isFavorite = favoriteSousCategories?.includes(sub?.slug);

                return (
                  <SousCategorieCard
                    key={sub?.id}
                    sub={sub}
                    categorySlug={categorySlug}
                    isSelected={!!isSelected}
                    isFavorite={!!isFavorite}
                    onSelect={() => handleSelectSub(sub)}
                    onToggleFavorite={onToggleFavorite}
                  />
                );
              })}
            </div>
          ) : (
            <p className="category-souscategorie-empty">
              Aucune sous-catégorie disponible pour le moment
            </p>
          )
        ) : (
          <div className="our-product-container-homepage category-souscategorie-modal-produits">
            <Row className="mt-1">
              {isFetchingProduits && <ProductItemsSkeleton />}

              {!isFetchingProduits &&
                !!produits.length &&
                produits.map((product: any, key: number) => (
                  <Col
                    sm={6}
                    xs={6}
                    md={4}
                    lg={4}
                    className="mb-3 col-product-item d-flex col-content-product-home"
                    key={product?.id || key}
                    style={{ display: "flex", flexDirection: "column" }}
                    onClickCapture={(e) => {
                      // Sur mobile : on affiche le produit dans le modal
                      // au lieu de laisser BestSellerCard rediriger vers /produit/:slug
                      if (isMobile) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectProduct(product);
                      }
                    }}
                  >
                    <div className="product-item-carousel">
                      <BestSellerCard item={product} key={product?.id || key} />
                    </div>
                  </Col>
                ))}

              {!isFetchingProduits && produits.length === 0 && (
                <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                  <p>Aucun produit disponible pour cette sous-catégorie</p>
                </div>
              )}
            </Row>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CategorySousCategorieModal;